import 'src/global.css';

// ----------------------------------------------------------------------

import { Router } from 'src/routes/sections';

import { useScrollToTop } from 'src/hooks/use-scroll-to-top';

import { CONFIG } from 'src/config-global';
import { LocalizationProvider } from 'src/locales';
import { I18nProvider } from 'src/locales/i18n-provider';
import { ThemeProvider } from 'src/theme/theme-provider';
import { ChatbotProvider } from 'src/components/chatbot/context';

import { Snackbar } from 'src/components/snackbar';
import { ProgressBar } from 'src/components/progress-bar';
import { MotionLazy } from 'src/components/animate/motion-lazy';
import { SettingsDrawer, defaultSettings, SettingsProvider } from 'src/components/settings';
import { useAuthContext } from 'src/auth/hooks';
import { PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { LoadingScreen } from 'src/components/loading-screen';

import { CheckoutProvider } from 'src/sections/checkout/context';

import { AuthProvider as JwtAuthProvider } from 'src/auth/context/jwt';
import { AuthProvider as Auth0AuthProvider } from 'src/auth/context/auth0';
import { AuthProvider as AmplifyAuthProvider } from 'src/auth/context/amplify';
import { AuthProvider as SupabaseAuthProvider } from 'src/auth/context/supabase';
import { AuthProvider as FirebaseAuthProvider } from 'src/auth/context/firebase';
import { useState, useEffect } from 'react';
import axiosInstance, { netScanAxiosInstance } from './utils/axios';
import { loadMsalConfig } from './auth-config';
import { ChatBotWrapper } from './components/chatbot';
import { QRScanButton } from './components/qr-scan-button';

// ----------------------------------------------------------------------

const AuthProvider =
  (CONFIG.auth.method === 'amplify' && AmplifyAuthProvider) ||
  (CONFIG.auth.method === 'firebase' && FirebaseAuthProvider) ||
  (CONFIG.auth.method === 'supabase' && SupabaseAuthProvider) ||
  (CONFIG.auth.method === 'auth0' && Auth0AuthProvider) ||
  JwtAuthProvider;

// ----------------------------------------------------------------------

function MsalWrapper({ children }) {
  const { user, isInitialized, authenticated } = useAuthContext(); // 👈 get token A from auth context
  const token = user?.accessToken; // 👈 assuming user object has accessToken
  const [msalInstance, setMsalInstance] = useState(null);

  useEffect(() => {
    const initMsal = async () => {
      try {
        // Set token A for backend call
        axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;
        netScanAxiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`;

        const msalConfig = await loadMsalConfig(); // 👈 fetch dynamic config
        const instance = new PublicClientApplication(msalConfig);
        setMsalInstance(instance);
      } catch (err) {
        console.error('Failed to initialize MSAL:', err);
      }
    };

    if (isInitialized && token && authenticated) {
      initMsal();
    }
  }, [authenticated, isInitialized, token]);

  // 👇 Don’t show MSAL until everything is ready (after login)
  if (!authenticated || !msalInstance) {
    return <>{children}</>; // just render app without MSAL
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

// ----------------------------------------------------------------------

export default function App() {
  useScrollToTop();

  return (
    <I18nProvider>
      <LocalizationProvider>
        <AuthProvider>
          <MsalWrapper>
            <ChatbotProvider>
              <SettingsProvider settings={defaultSettings}>
                <ThemeProvider>
                  <MotionLazy>
                    <CheckoutProvider>
                      <Snackbar />
                      <ProgressBar />
                      <SettingsDrawer />
                      <Router />
                      <QRScanButton />
                      <ChatBotWrapper />
                    </CheckoutProvider>
                  </MotionLazy>
                </ThemeProvider>
              </SettingsProvider>
            </ChatbotProvider>
          </MsalWrapper>
        </AuthProvider>
      </LocalizationProvider>
    </I18nProvider>
  );
}
